"use client";
import { useCallback, useState } from "react";
import type { Agency } from "./useAgencies";

export type TeamSizeBucket = "1-10" | "11-50" | "51-200" | "200+";

const BUCKET_RANGES: Record<TeamSizeBucket, [number, number]> = {
  "1-10": [1, 10],
  "11-50": [11, 50],
  "51-200": [51, 200],
  "200+": [201, Number.POSITIVE_INFINITY],
};

export const TEAM_SIZE_BUCKETS: TeamSizeBucket[] = [
  "1-10",
  "11-50",
  "51-200",
  "200+",
];

export type AgencyFilters = {
  countries: string[];
  fields: string[];
  services: string[];
  minRating: number;
  teamSizes: TeamSizeBucket[];
};

export const defaultAgencyFilters: AgencyFilters = {
  countries: [],
  fields: [],
  services: [],
  minRating: 0,
  teamSizes: [],
};

export function activeAgencyFilterCount(f: AgencyFilters): number {
  let n = 0;
  if (f.countries.length > 0) n += 1;
  if (f.fields.length > 0) n += 1;
  if (f.services.length > 0) n += 1;
  if (f.minRating > 0) n += 1;
  if (f.teamSizes.length > 0) n += 1;
  return n;
}

function teamSizeMatches(
  size: number | null | undefined,
  buckets: TeamSizeBucket[]
): boolean {
  if (buckets.length === 0) return true;
  if (size == null) return false;
  return buckets.some((b) => {
    const [lo, hi] = BUCKET_RANGES[b];
    return size >= lo && size <= hi;
  });
}

export function useAgencyFilters(
  initial: AgencyFilters = defaultAgencyFilters
) {
  const [filters, setFiltersState] = useState<AgencyFilters>(initial);

  const setFilters = useCallback(
    (
      update:
        | Partial<AgencyFilters>
        | ((prev: AgencyFilters) => AgencyFilters)
    ) => {
      setFiltersState((prev) =>
        typeof update === "function" ? update(prev) : { ...prev, ...update }
      );
    },
    []
  );

  const clear = useCallback(() => {
    setFiltersState(defaultAgencyFilters);
  }, []);

  const apply = useCallback(
    (agencies: Agency[] | undefined): Agency[] => {
      if (!agencies) return [];
      return agencies.filter((a) => {
        // Countries — match if at least one of agency's countries is in selected list
        if (filters.countries.length > 0) {
          const ac = Array.isArray(a.countriesCovered) ? a.countriesCovered : [];
          if (!ac.some((c) => filters.countries.includes(c))) return false;
        }

        // Fields — same logic
        if (filters.fields.length > 0) {
          const af = Array.isArray(a.fieldsCovered) ? a.fieldsCovered : [];
          if (!af.some((f) => filters.fields.includes(f))) return false;
        }

        // Services — same logic
        if (filters.services.length > 0) {
          const as = Array.isArray(a.servicesOffered) ? a.servicesOffered : [];
          if (!as.some((s) => filters.services.includes(s))) return false;
        }

        // Rating floor
        if ((a.rating ?? 0) < filters.minRating) return false;

        // Team size buckets
        if (!teamSizeMatches(a.teamSize, filters.teamSizes)) return false;

        return true;
      });
    },
    [filters]
  );

  return {
    filters,
    setFilters,
    apply,
    clear,
    activeFilterCount: activeAgencyFilterCount(filters),
  };
}
